/**
 * Bucket Allocation View
 * Displays strategic bucket allocation with account assignments
 */
import { useState } from 'react';
import { useBucketAllocation } from '@/hooks/useAllocation';
import { useAccounts } from '@/hooks/useAccounts';
import { useAllocationStore } from '@/store/allocationStore';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import {
  type StrategicRole,
  type BucketAllocation,
} from '@/types/allocation';

export default function BucketAllocationView() {
  const { data, isLoading } = useBucketAllocation();
  const { data: accountsData } = useAccounts();
  const setAssigningAccountId = useAllocationStore((state) => state.setAssigningAccountId);
  const setActiveTab = useAllocationStore((state) => state.setActiveTab);

  // Track expanded buckets
  const [expandedBuckets, setExpandedBuckets] = useState<Set<string>>(new Set());

  const toggleBucket = (role: string) => {
    setExpandedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(role)) {
        next.delete(role);
      } else {
        next.add(role);
      }
      return next;
    });
  };

  // Create a map of account IDs to account names
  const accountMap = new Map(
    (accountsData ?? []).map((acc) => [acc.id, acc])
  );

  if (isLoading || !data) {
    return <BucketSkeleton />;
  }

  const { allocation } = data;

  return (
    <div className="space-y-6">
      {/* Summary card */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Strategic Buckets
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Your assets organized by strategic purpose
          </p>

          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Assets</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                ${formatNumber(allocation.totalValueUsd)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Unassigned Accounts</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {allocation.unassignedAccountCount}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Bucket breakdown */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {allocation.buckets.map((bucket) => (
          <BucketCard
            key={bucket.strategicRole}
            bucket={bucket}
            isExpanded={expandedBuckets.has(bucket.strategicRole)}
            onToggle={() => toggleBucket(bucket.strategicRole)}
            accountMap={accountMap}
            onAssignAccount={() => setAssigningAccountId('new')}
            onViewTargets={() => setActiveTab('targets')}
          />
        ))}
      </div>

      {/* Unassigned accounts warning */}
      {allocation.unassignedAccountCount > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <div className="p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <WarningIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  {allocation.unassignedAccountCount} account(s) not assigned to buckets
                </p>
                <p className="text-sm text-yellow-700 mt-0.5">
                  Assign accounts to strategic buckets for complete allocation tracking.
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setAssigningAccountId('new')}
            >
              Assign Accounts
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

interface BucketCardProps {
  bucket: BucketAllocation;
  isExpanded: boolean;
  onToggle: () => void;
  accountMap: Map<string, { id: string; name: string; type: string; balance: number }>;
  onAssignAccount: () => void;
  onViewTargets: () => void;
}

function BucketCard({
  bucket,
  isExpanded,
  onToggle,
  accountMap,
  onAssignAccount,
  onViewTargets,
}: BucketCardProps) {
  const currentPct = parseFloat(bucket.currentPercentage);
  const targetPct = bucket.targetPercentage
    ? parseFloat(bucket.targetPercentage)
    : null;

  // Get account details for this bucket
  const bucketAccounts = bucket.accountIds
    .map((id) => accountMap.get(id))
    .filter((acc): acc is NonNullable<typeof acc> => acc !== undefined);

  return (
    <Card className="hover:shadow-md transition-shadow">
      {/* Clickable header */}
      <button
        className="w-full p-4 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-t-lg"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: getBucketColor(bucket.strategicRole) }}
            />
            <ChevronIcon
              className={`h-4 w-4 text-gray-400 transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </div>
          <span className="text-sm font-medium text-gray-900">
            {currentPct.toFixed(1)}%
          </span>
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          {bucket.displayName}
        </h3>

        <p className="mt-2 text-2xl font-semibold text-gray-900">
          ${formatNumber(bucket.currentAmount)}
        </p>

        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-gray-500">
            {bucket.accountCount} account{bucket.accountCount !== 1 ? 's' : ''}
          </span>
          {targetPct !== null && (
            <span className="text-gray-600">
              Target: {targetPct.toFixed(1)}%
            </span>
          )}
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${Math.min(currentPct, 100)}%`,
              backgroundColor: getBucketColor(bucket.strategicRole),
            }}
          />
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Account list */}
          {bucketAccounts.length > 0 ? (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Accounts in this bucket
              </p>
              <ul className="divide-y divide-gray-100">
                {bucketAccounts.map((account) => (
                  <li key={account.id} className="py-2 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{account.name}</p>
                      <p className="text-xs text-gray-500">{account.type}</p>
                    </div>
                    <span className="text-sm text-gray-600">
                      ${formatNumber(account.balance)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="mt-3 text-sm text-gray-500 italic">
              No accounts assigned to this bucket yet.
            </p>
          )}

          {/* Action buttons */}
          <div className="mt-4 flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onAssignAccount}>
              Assign Account
            </Button>
            <Button variant="ghost" size="sm" onClick={onViewTargets}>
              View Targets
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function BucketSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="grid grid-cols-2 gap-6">
            <div className="h-12 bg-gray-200 rounded" />
            <div className="h-12 bg-gray-200 rounded" />
          </div>
        </div>
      </Card>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <div className="p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
              <div className="h-8 bg-gray-200 rounded w-2/3 mb-2" />
              <div className="h-6 bg-gray-200 rounded w-1/2" />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

function getBucketColor(role: StrategicRole): string {
  const colors: Record<StrategicRole, string> = {
    emergency_fund: '#10B981',
    short_term_savings: '#3B82F6',
    retirement_pretax: '#F59E0B',
    retirement_roth: '#F97316',
    retirement_taxable: '#EAB308',
    education: '#EC4899',
    healthcare: '#8B5CF6',
    general_investment: '#6B7280',
  };
  return colors[role] || '#9CA3AF';
}

function formatNumber(value: string | number | null | undefined): string {
  if (value == null || value === '') return '0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}
